/**
 * Per-product SEO derivation.
 *
 * The CMS schema declares `seo_title` and `seo_description` fields on every
 * product, but at launch 256/256 SKUs ship them empty. We compute a conservative
 * fallback from real signals (name, brand, category_label, hook) so the head
 * tags are never blank and Google sees distinct titles/descriptions per fiche.
 *
 * Rules:
 * - Title ≤ 60 chars, brand-first when a brand exists, never repeats "Frites Dorées"
 *   (the hook appends " — Frites Dorées" downstream).
 * - Description ≤ 155 chars, B2B-oriented, ends on a delivery / devis cue when
 *   nothing better is in the source data. Never invents specs.
 */

const trim = (s, max) => {
  if (!s) return "";
  const t = s.trim();
  if (t.length <= max) return t;
  // try to cut on a word boundary close to the max
  const cut = t.slice(0, max - 1).lastIndexOf(" ");
  return (cut > max - 20 ? t.slice(0, cut) : t.slice(0, max - 1)).trim() + "…";
};

const stripDouble = (s) => s.replace(/\s+/g, " ").replace(/[—\-]\s*[—\-]/g, "—").trim();

export function productSeo(product) {
  if (!product) return { title: null, description: null };

  // --- Title ---
  // Prefer manually-written seo_title. Otherwise compute a B2B-leaning fallback
  // that earns at least one target keyword ("grossiste" or "fournisseur").
  let title = (product.seo_title || "").trim();
  if (!title) {
    const namePart = product.name;
    if (product.brand && !product.name?.toLowerCase().includes(product.brand.toLowerCase())) {
      title = `${namePart} — ${product.brand} grossiste pro`;
    } else {
      title = `${namePart} — Grossiste alimentaire B2B restauration rapide`;
    }
  }
  title = trim(stripDouble(title), 60);

  // --- Description ---
  // Per-category framing for the fallback so every fiche carries the most
  // relevant target keyword ("grossiste frites surgelées" for frites,
  // "sauces pro restauration rapide" for sauces, etc.). A manual
  // seo_description on a fiche always wins over the fallback.
  let description = (product.seo_description || "").trim();
  if (!description) {
    const parts = [];
    if (product.hook) parts.push(product.hook.trim());
    const cat = (product.category || "").toLowerCase();
    if (cat === "frites") {
      parts.push("Frites surgelées grossiste pour fast-food et restauration rapide.");
    } else if (cat === "sauce") {
      parts.push("Sauces pro pour restauration rapide et snack.");
    } else if (cat === "viandes") {
      parts.push("Viandes grossiste pour fast-food et restauration rapide.");
    } else if (cat === "volailles") {
      parts.push("Volailles pro pour fast-food et restauration rapide.");
    } else if (cat === "emballages") {
      parts.push("Emballages alimentaires professionnels pour restauration rapide.");
    } else if (cat === "dessert") {
      parts.push("Desserts pro pour fast-food et restauration rapide.");
    } else if (cat === "tortillas-et-pains") {
      parts.push("Pains et tortillas pro pour fast-food et restauration rapide.");
    } else if (product.category_label) {
      parts.push(`${product.category_label} grossiste pour fast-food et restauration rapide.`);
    } else {
      parts.push("Produit grossiste pour fast-food et restauration rapide.");
    }
    parts.push("Livraison pros IDF · Devis B2B 24h.");
    description = parts.filter(Boolean).join(" ");
  }
  description = trim(stripDouble(description), 155);

  return { title, description };
}

/**
 * Schema.org Product JSON-LD for a product detail page.
 * No `offers` block because the site doesn't expose prices — adding fake
 * offers would trigger a Google warning.
 */
export function productJsonLd(product, siteUrl = "https://www.fritesdorees.fr") {
  if (!product) return null;
  const url = `${siteUrl}/produit/${product.slug}`;
  const image = product.image
    ? product.image.startsWith("http")
      ? product.image
      : `${siteUrl}${product.image.startsWith("/") ? product.image : "/products/" + product.image}`
    : undefined;

  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${url}#product`,
    "name": product.name,
    "url": url,
    "category": product.category_label || product.category,
  };
  if (product.brand) ld.brand = { "@type": "Brand", "name": product.brand };
  if (image) ld.image = image;
  const seo = productSeo(product);
  if (seo.description) ld.description = seo.description;
  return ld;
}

/**
 * BreadcrumbList JSON-LD for a product detail page.
 * Category step uses the new /catalogue/<url-slug> path (see
 * lib/category-slugs.js) so the schema mirrors the canonical URLs.
 */
import { categoryPath } from "./category-slugs";

export function productBreadcrumbLd(product, siteUrl = "https://www.fritesdorees.fr") {
  if (!product) return null;
  const items = [
    { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${siteUrl}/` },
    { "@type": "ListItem", "position": 2, "name": "Catalogue", "item": `${siteUrl}/catalogue` },
  ];
  if (product.category && product.category_label) {
    items.push({
      "@type": "ListItem",
      "position": 3,
      "name": product.category_label,
      "item": `${siteUrl}${categoryPath(product.category)}`,
    });
  }
  items.push({
    "@type": "ListItem",
    "position": items.length + 1,
    "name": product.name,
    "item": `${siteUrl}/produit/${product.slug}`,
  });
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": items };
}
